import React, { useState, memo } from 'react';
import { Handle, Position } from 'reactflow';
import { createPortal } from 'react-dom';
import { BoltIcon, ClockIcon, CalendarIcon } from '@heroicons/react/24/outline';
import TriggerNodeModal from './TriggerNodeModal';

const TriggerNode = ({ id, data }) => {
  const [showEditModal, setShowEditModal] = useState(false);

  const handleDoubleClick = (e) => {
    e.preventDefault();
    setShowEditModal(true);
  };

  // Format schedule display
  const getScheduleDisplay = () => {
    if (data.triggerType === 'manual') {
      return 'Manual trigger';
    }

    switch (data.scheduleType) {
      case 'daily':
        return `Daily at ${data.dailyTime}`;
      case 'weekly':
        return `Every ${data.weeklyDay} at ${data.weeklyTime}`;
      case 'monthly':
        return `Monthly on day ${data.monthlyDay} at ${data.monthlyTime}`;
      default:
        return 'Schedule not set';
    }
  };

  return (
    <div 
      className="bg-dark-200 p-4 rounded-lg min-w-[200px] min-h-[200px]"
      onDoubleClick={handleDoubleClick}
    >
      <Handle
        type="source"
        position={Position.Right}
        className="!w-5 !h-5 !bg-dark-300 border-2 border-[#2AA365]"
        style={{ 
          right: '-12px',
          zIndex: 1,
          transform: 'translateY(-50%)'
        }}
      />
      
      <div className="flex flex-col items-center space-y-5">
        {/* Icon Section - Using light grey color */}
        <div className="mb-2">
          <BoltIcon className="w-16 h-16 text-gray-400" />
        </div>

        {/* Content Section */}
        <div className="w-full text-center">
          <div className="font-medium text-lg">{data.label}</div>
          {data.description && (
            <div className="text-sm text-gray-400 mt-1">{data.description}</div>
          )}
          
          {/* Trigger details */}
          <div className="mt-3 space-y-2 text-xs text-gray-400">
            {data.triggerType === 'scheduled' && (
              <div className="flex items-center justify-center">
                <ClockIcon className="w-3 h-3 mr-1" />
                <span>{getScheduleDisplay()}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showEditModal && createPortal(
        <TriggerNodeModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          node={{ id, data }}
          onUpdate={data.onUpdateTiming}
        />,
        document.body
      )}
    </div>
  );
};

export default memo(TriggerNode);

export const MenuItem = ({ onSelect }) => {
  return (
    <button
      onClick={() => onSelect({
        type: 'trigger',
        name: 'Workflow Trigger',
        label: 'Workflow Trigger',
        category: 'trigger',
        triggerType: 'manual',
        scheduleType: 'datetime',
        description: 'Starts the workflow execution',
        onDelete: null,
        onRename: null,
        onUpdateTiming: null
      })}
      className="flex items-center gap-3 p-4 hover:bg-dark-300 w-full transition-colors rounded-lg"
    >
      <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
        <BoltIcon className="w-6 h-6 text-green-500" />
      </div>
      <div className="flex-1 text-left">
        <h3 className="font-medium">Trigger</h3>
        <p className="text-sm text-gray-400">Start point for workflow execution</p>
      </div>
    </button>
  );
}; 